/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

import { calculateDuration } from '../common';
import { formatDuration } from '../../lib/utils';

// Positional constants for the turn chain layout. Shared so other modules
// (e.g., the pagination placeholder injection) can position synthetic nodes
// in the same coordinate system as the real turn nodes.
export const TURN_X = -100;
export const TURN_START_Y = 100;
export const TURN_VERTICAL_SPACING = 150;
export const GROUP_X_OFFSET = 250;

/**
 * Extract icon source URL from questionsData based on span_id
 */
function getIconSource(
  spanId: string | undefined,
  questionsData: any[],
  iconMap: Record<string, string>
): string {
  const defaultIcon = iconMap['default'] || '/logos/provider/not-available.svg';

  if (!spanId || !questionsData || questionsData.length === 0) {
    return defaultIcon;
  }

  // Find matching span in questions data
  const spanData = questionsData.find(
    (q: any) => q.context?.span_id === spanId
  );

  if (!spanData?.attributes?.['entity.1.type']) {
    return defaultIcon;
  }

  const entityType = spanData.attributes['entity.1.type'];
  return iconMap[entityType] ?? defaultIcon;
}

/**
 * Infer node type from node type property or id pattern
 * Normalizes sub-types like 'mcp.tool' → 'tool', 'mcp.agent' → 'agent'
 * Patterns: 'end:', 'query:', 'agent:', 'tool:'
 */
function inferNodeType(node: any): string {
  if (node.type) {
    const t = node.type.toLowerCase();
    // Normalize dot-prefixed sub-types (e.g., 'mcp.tool' → 'tool', 'mcp.agent' → 'agent')
    if (t.includes('.')) {
      const base = t.split('.').pop()!;
      if (['turn', 'end', 'agent', 'tool'].includes(base)) return base;
    }
    return t;
  }

  // for nodes which do not have type property
  const id = node.id || '';
  if (id.startsWith('end:')) return 'end';
  if (id.startsWith('query:')) return 'turn';
  if (id.startsWith('agent:')) return 'agent';
  if (id.startsWith('tool:')) return 'tool';

  return 'unknown';
}

/**
 * Build tray view data for an INVOKES edge.
 * factIds and turnIds are arrays to support future multi-value cases.
 */
function buildInvokesTrayData(edge: any): Record<string, any> {
  return {
    tray: {
      edgeType: edge.edge_type,
      factIds: edge.fact_id != null ? [edge.fact_id] : [],
      turnIds: edge.turn_id != null ? [edge.turn_id] : [],
      status: edge.status ?? null,
      input: edge.input ?? null,
      output: edge.output ?? null,
    },
  };
}

/**
 * Build tray view data for DELEGATES_TO, USES_TOOL, and HANDOFF edges.
 * Follows the three-phase execution model: decision → execution → receipt.
 *
 * Top-level input comes from the decision phase input.
 * Top-level output comes from the receipt phase output.
 */
function buildThreePhaseTrayData(edge: any): Record<string, any> {
  const decision = edge.decision ?? null;
  const execution = edge.execution ?? null;
  const receipt = edge.receipt ?? null;

  return {
    tray: {
      edgeType: edge.edge_type,
      factIds: edge.fact_id != null ? [edge.fact_id] : [],
      turnIds: edge.turn_id != null ? [edge.turn_id] : [],
      status: edge.status ?? null,
      input: edge?.input ?? null,
      output: edge?.output ?? null,
      decision: decision
        ? {
            status: decision.status ?? null,
            startTime: decision.start_time ?? null,
            endTime: decision.end_time ?? null,
            input: decision.input ?? null,
            output: decision.output ?? null,
          }
        : null,
      execution: execution
        ? {
            status: execution.status ?? null,
            startTime: execution.start_time ?? null,
            endTime: execution.end_time ?? null,
            input: execution.input ?? null,
            output: execution.output ?? null,
          }
        : null,
      receipt: receipt
        ? {
            status: receipt.status ?? null,
            startTime: receipt.start_time ?? null,
            endTime: receipt.end_time ?? null,
            input: receipt.input ?? null,
            output: receipt.output ?? null,
          }
        : null,
    },
  };
}

/**
 * Build tray data for a turn node derived from its matching INVOKES edge.
 * All fields are captured as full objects to support span reference resolution.
 */
function buildTurnNodeTrayFromInvokesEdge(invokeEdge: any): Record<string, any> {
  return {
    edgeType: invokeEdge.edge_type,
    status: invokeEdge.status ?? null,
    input: invokeEdge.input ?? null,
    output: invokeEdge.output ?? null,
    factId: invokeEdge.fact_id ?? null,
    startTime: invokeEdge.start_time ?? null,
    endTime: invokeEdge.end_time ?? null,
  };
}

/**
 * Enrich a raw edge object with tray view data based on its edge_type.
 * Returns the tray payload to be spread into the GoJS link data object.
 */
function buildEdgeTrayData(edge: any): Record<string, any> {
  const edgeType = edge.edge_type?.toUpperCase();
  switch (edgeType) {
    case 'INVOKES':
      return buildInvokesTrayData(edge);
    case 'DELEGATES_TO':
    case 'USES_TOOL':
    case 'HANDOFF':
      return buildThreePhaseTrayData(edge);
    default:
      return {};
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Span reference resolution helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a lookup map of span_id → span object from the spanDetails structure.
 * Iterates all trace entries and flattens their spans arrays for O(1) access.
 *
 */
function buildSpanLookup(
  spanDetails: any
): Map<string, any> {
  const lookup = new Map<string, any>();

  const traceSpanDetails = spanDetails?.traceSpanDetails;
  if (!traceSpanDetails) return lookup;

  for (const traceId of Object.keys(traceSpanDetails)) {
    const spans: any[] = traceSpanDetails[traceId]?.spans ?? [];
    for (const span of spans) {
      if (span.span_id) {
        lookup.set(span.span_id, span);
      }
    }
  }

  return lookup;
}

function extractAgenticScope(span: any): Record<string, string> {
  const result: Record<string, string> = {};
  const attrs = span?.attributes ?? {};
  for (const key of Object.keys(attrs)) {
    if (key.startsWith('scope.agentic.')) {
      result[key] = attrs[key];
    }
  }
  return result;
}

/**
 * Resolve a single span reference object by enriching it with:
 * - start_time and end_time from the matched span
 * - value: the event payload resolved by the field path
 *
 * Field path format: "events.<eventNameSegment>.<key>"
 * e.g. "events.data.input" → event name "data.input", key "input"
 *
 * Returns a new enriched object; the original is not mutated.
 */
function resolveSpanRef(
  spanRef: any,
  spanLookup: Map<string, any>
): any {
  if (!spanRef || typeof spanRef !== 'object' || !spanRef.span_id) {
    return spanRef;
  }

  const span = spanLookup.get(spanRef.span_id);
  if (!span) return { ...spanRef };

  const field: string | undefined | null = spanRef.field;
  const enriched: any = { ...spanRef };

  if (field === null || field === undefined) {
    // No field specified — value is explicitly null
    enriched.value = null;
  } else if (field === 'start_time') {
    // Only the start timestamp is relevant for this reference
    enriched.start_time = span.start_time ?? null;
  } else if (field === 'end_time') {
    // Only the end timestamp is relevant for this reference
    enriched.end_time = span.end_time ?? null;
  } else if (field === 'status') {
    // Read the status object directly from the span
    enriched.value = span.status ?? null;
  } else if (/^entity\.\d+\..+$/.test(field)) {
    // entity.<n>.<property> — 1-based index into span.attributes.entity array
    // e.g. "entity.1.description" → span.attributes.entity[0].description
    const parts = field.split('.');
    const entityIndex = parseInt(parts[1], 10) - 1;
    const property = parts.slice(2).join('.');
    const entityArray: any[] = span.attributes?.entity ?? [];
    enriched.value = entityArray[entityIndex]?.[property] ?? null;
  } else if (field && field.startsWith('events.')) {
    // For event fields, resolve only the event value — no timestamps or duration
    const segments = field.split('.');
    if (segments.length >= 3) {
      const key = segments[segments.length - 1];

      // Event names in spanDetails are always "data.<key>" (e.g. "data.input", "data.output").
      // The field may say "events.attributes.input" (deprecated) or "events.data.input" —
      // in both cases we normalise the lookup name to "data.<key>" and fall back on
      // the attributes equivalent if data is not present on the matched event.
      //
      // Special case for output:
      //   primary event name  → "data.output"
      //   fallback event name → "attributes.response"  (there is no "attributes.output")
      const isOutput = key === 'output';
      const canonicalEventName = `data.${key}`;
      const fallbackEventName = isOutput ? 'attributes.response' : `attributes.${key}`;

      const events: any[] = span.events ?? [];
      const matchedEvent =
        events.find((e: any) => e.name === canonicalEventName) ??
        events.find((e: any) => e.name === fallbackEventName);

      if (matchedEvent) {
        // For output, the deprecated attribute key is "response" not "output"
        const value =
          matchedEvent.data?.[key] ??
          matchedEvent.attributes?.[key] ??
          (isOutput ? matchedEvent.attributes?.response : null) ??
          null;
        enriched.value = value;
      }
    }
  }

  return enriched;
}

/**
 * Recursively walk a tray object and resolve every span reference found
 * (objects with a span_id property) using the provided span lookup.
 *
 * Returns a new deep-copied tray object with all span refs enriched.
 */
function resolveTraySpanRefs(tray: any, spanLookup: Map<string, any>): any {
  if (!tray || typeof tray !== 'object') return tray;

  // If this object itself is a span reference, resolve it
  if (tray.span_id !== undefined) {
    return resolveSpanRef(tray, spanLookup);
  }

  // Otherwise recurse into all properties
  const result: any = {};
  for (const key of Object.keys(tray)) {
    const val = tray[key];
    if (Array.isArray(val)) {
      result[key] = val.map((item) => resolveTraySpanRefs(item, spanLookup));
    } else if (val && typeof val === 'object') {
      result[key] = resolveTraySpanRefs(val, spanLookup);
    } else {
      result[key] = val;
    }
  }

  // If this object is a phase (decision/execution/receipt), compute its duration
  // from the resolved startTime and endTime span references
  if (result.startTime?.start_time && result.endTime?.end_time) {
    result.durationMs = calculateDuration(result.startTime.start_time, result.endTime.end_time);
  }

  return result;
}

/**
 * Enriches a deep copy of the linkDataArray by resolving all span references
 * inside each link's `tray` object against the provided spanDetails.
 *
 * Links without a `tray` property are included unchanged.
 * The original linkDataArray is never mutated.
 *
 * @param linkDataArray - GoJS link data array (output of projectTurnChildren)
 * @param spanDetails   - spanDetails object from the component (contains traceSpanDetails)
 * @returns A new linkDataArray with all tray span references enriched
 */
export function enrichLinkDataWithSpanDetails(
  linkDataArray: any[],
  spanDetails: any
): any[] {
  if (!linkDataArray || !spanDetails) return linkDataArray ?? [];

  const spanLookup = buildSpanLookup(spanDetails);

  return linkDataArray.map((link) => {
    // Deep copy the link
    const linkCopy = JSON.parse(JSON.stringify(link));

    if (!linkCopy.tray) return linkCopy;

    linkCopy.tray = resolveTraySpanRefs(linkCopy.tray, spanLookup);

    return linkCopy;
  });
}

/**
 * Enriches a deep copy of the nodeDataArray by resolving span references
 * on agent (systemPrompt) and tool (description) nodes against spanDetails.
 *
 *
 * @param nodeDataArray - GoJS node data array (output of projectTurnChildren)
 * @param spanDetails   - spanDetails object from the component (contains traceSpanDetails)
 * @returns A new nodeDataArray with agent/tool span references enriched
 */
export function enrichNodeDataWithSpanDetails(
  nodeDataArray: any[],
  spanDetails: any
): any[] {
  if (!nodeDataArray || !spanDetails) return nodeDataArray ?? [];

  const spanLookup = buildSpanLookup(spanDetails);

  return nodeDataArray.map((node) => {
    // Deep copy the node
    const nodeCopy = JSON.parse(JSON.stringify(node));

    // Resolve systemPrompt for agent nodes
    if (nodeCopy.rawType === 'agent' && nodeCopy.systemPrompt?.span_id) {
      nodeCopy.systemPrompt = resolveSpanRef(nodeCopy.systemPrompt, spanLookup);

      // ── inject scope.agentic.* into tray ──
      const span = spanLookup.get(nodeCopy.systemPrompt.span_id);
      if (span) {
        nodeCopy.tray = { ...nodeCopy.tray, ...extractAgenticScope(span) };
      }
    }

    // Resolve description for tool nodes
    if (nodeCopy.rawType === 'tool' && nodeCopy.description?.span_id) {
      nodeCopy.description = resolveSpanRef(nodeCopy.description, spanLookup);

      // ── inject scope.agentic.* into tray ──
      const span = spanLookup.get(nodeCopy.description.span_id);
      if (span) {
        nodeCopy.tray = { ...nodeCopy.tray, ...extractAgenticScope(span) };
      }
    }

    // Resolve tray for turn nodes
    if (nodeCopy.rawType === 'turn' && nodeCopy.tray) {
      nodeCopy.tray = resolveTraySpanRefs(nodeCopy.tray, spanLookup);
    }

    return nodeCopy;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Turn-only transform & projection functions (step 1 of incremental split)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Transform the turns API response into a normalized structure
 * with turn nodes, an end node, and synthetic turn-sequence edges.
 *
 * API response shape (NarrativeTurnsData):
 * {
 *   fact_id: string,
 *   num_turns: number,
 *   start_time: string,
 *   end_time: string,
 *   turn_ids: Array<{ [turn_id: string]: { start_time, end_time, trace_id } }>
 * }
 *
 * @param turnsData - Raw turns API response
 * @returns Normalized graph data with only turn + end nodes and synthetic edges
 */
export function transformTurnsResponse(
  turnsData: any
): { graph_metadata: any; nodes: any[]; edges: any[] } | null {
  if (!turnsData) {
    console.warn('[transformTurnsResponse] No turns data provided');
    return null;
  }

  // Handle array response (API may wrap in array)
  if (Array.isArray(turnsData)) {
    if (turnsData.length === 0) {
      console.warn('[transformTurnsResponse] Empty array response');
      return null;
    }
    turnsData = turnsData[0];
  }

  if (!turnsData.turn_ids || !Array.isArray(turnsData.turn_ids)) {
    console.warn('[transformTurnsResponse] Invalid turns data - missing turn_ids array');
    return null;
  }

  const factId = turnsData.fact_id;
  const turnEntries = turnsData.turn_ids;


  // Build turn nodes from the turn_ids array
  // Each entry is { [turn_id]: { start_time, end_time, trace_id } }
  const nodes: any[] = turnEntries.map((entry: any, index: number) => {
    const turnId = Object.keys(entry)[0];
    const details = entry[turnId];
    return {
      id: turnId,
      type: 'turn',
      name: turnId,
      turn_id: turnId,
      turn_index: index,
      fact_id: factId,
      start_time: details.start_time,
      end_time: details.end_time,
      trace_id: details.trace_id,
      status: details.status,
    };
  });

  // Add an end node
  nodes.push({
    id: `end:${factId}`,
    type: 'end',
    name: 'End',
    fact_id: factId,
  });

  // Build synthetic turn-sequence edges + last-turn-to-end edge
  const edges: any[] = [];
  for (let i = 0; i < turnEntries.length - 1; i++) {
    const fromId = Object.keys(turnEntries[i])[0];
    const toId = Object.keys(turnEntries[i + 1])[0];
    edges.push({
      id: `synthetic_turn_${fromId}__${toId}`,
      from_node: fromId,
      to_node: toId,
      edge_type: 'TURN_SEQUENCE',
      is_concurrent: false,
    });
  }

  // Edge from last turn to end node
  if (turnEntries.length > 0) {
    const lastTurnId = Object.keys(turnEntries[turnEntries.length - 1])[0];
    edges.push({
      id: `synthetic_turn_${lastTurnId}__end:${factId}`,
      from_node: lastTurnId,
      to_node: `end:${factId}`,
      edge_type: 'TURN_SEQUENCE',
      is_concurrent: false,
    });
  }

  return {
    graph_metadata: {
      fact_id: factId,
      num_turns: turnsData.num_turns,
      start_time: turnsData.start_time,
      end_time: turnsData.end_time,
    },
    nodes,
    edges,
  };
}

/**
 * Project turn-only graph data into GoJS-compatible nodeDataArray and linkDataArray.
 * Only produces the vertical turn chain + end node. No agents/tools/groups.
 * The first turn is marked isExpanded = true (caller should trigger children API).
 *
 * @param turnsGraphData - Output of transformTurnsResponse
 * @param questionsData  - QUESTIONS dataset for icon lookup
 * @param iconMap        - Map of entity types to icon URLs
 */
export function projectTurnGraph(
  turnsGraphData: { graph_metadata: any; nodes: any[]; edges: any[] } | null,
  questionsData: any[] = [],
  iconMap: Record<string, string> = {},
): { nodeDataArray: any[]; linkDataArray: any[] } {
  const empty = { nodeDataArray: [], linkDataArray: [] };

  if (!turnsGraphData || !turnsGraphData.nodes || !turnsGraphData.edges) {
    console.warn('[projectTurnGraph] Invalid or missing turnsGraphData');
    return empty;
  }

  // Separate turn vs end nodes
  const turnNodes: any[] = [];
  const endNodes: any[] = [];
  turnsGraphData.nodes.forEach((node: any) => {
    const nodeType = inferNodeType(node);
    if (nodeType === 'turn') turnNodes.push(node);
    else if (nodeType === 'end') endNodes.push(node);
  });

  // Sort turn nodes by turn_index (already sequential from transformTurnsResponse)
  const sortedTurnNodes = turnNodes
    .slice()
    .sort((a, b) => (a.turn_index ?? 0) - (b.turn_index ?? 0));

  const firstTurnNodeId = sortedTurnNodes.length > 0 ? sortedTurnNodes[0].id : null;
  const factId = turnsGraphData.graph_metadata?.fact_id || 'unknown';
  const startNodeKey = `start:${factId}`;

  // Build node data array — start node first (hardcoded, always present)
  const nodeDataArray: any[] = [];
  nodeDataArray.push({
    key: startNodeKey,
    label: '',
    name: 'Start',
    category: 'start',
    rawType: 'start',
    location: `${TURN_X - 14} ${TURN_START_Y - TURN_VERTICAL_SPACING}`,
    source: iconMap['start-icon'] || '/logos/play.svg',
  });

  sortedTurnNodes.forEach((node, index) => {
    const fullLabel = node.turn_id || node.name;
    const label = fullLabel?.length > 8 ? fullLabel.slice(0, 8) + '...' : fullLabel;
    const y = TURN_START_Y + index * TURN_VERTICAL_SPACING;

    // Icon lookup via span_id if available
    const defaultIcon = iconMap['default'] || '/logos/provider/not-available.svg';
    const turnIcon = iconMap['okahu-icon'] || '/logos/okahu.svg';
    let iconSource = defaultIcon;
    if (node.start_time?.span_id) {
      iconSource = getIconSource(node.start_time.span_id, questionsData, iconMap);
    }

    nodeDataArray.push({
      key: node.id,
      label,
      name: node.name,
      tooltip: fullLabel,
      category: 'turn',
      rawType: 'turn',
      metadata: node.metadata,
      source: node.type === 'turn' ? turnIcon : iconSource,
      isExpanded: node.id === firstTurnNodeId,
      traceId: node.trace_id,
      factId: node.fact_id,
      status: node.status,
      duration: (() => {
        const rawDur = calculateDuration(node.start_time, node.end_time);
        const parsed = parseFloat(rawDur);
        return isNaN(parsed) ? rawDur : formatDuration(parsed);
      })(),
      location: `${TURN_X} ${y}`,
    });
  });

  // End node positioned below the last turn
  endNodes.forEach((node) => {
    const y = TURN_START_Y + sortedTurnNodes.length * TURN_VERTICAL_SPACING;
    nodeDataArray.push({
      key: node.id,
      label: 'End',
      name: node.name,
      tooltip: '',
      category: 'end',
      rawType: 'end',
      metadata: node.metadata,
      source: iconMap['end-icon'] || '/logos/stop.svg',
      location: `${TURN_X - 14} ${y}`,
    });
  });

  // Build a set of turn node keys — used to stamp edgeLabel on outgoing edges
  const turnNodeIdSet = new Set(sortedTurnNodes.map((n) => n.id));

  // Build link data array — edgeLabel driven by whether the `from` node is a turn
  const linkDataArray = turnsGraphData.edges.map((edge: any) => {
    const fromNode = edge.from ?? edge.from_node;
    const toNode = edge.to ?? edge.to_node;

    return {
      id: edge.id,
      from: fromNode,
      to: toNode,
      fromPort: 'bottom',
      toPort: 'top',
      label: '',
      tooltip: '',
      edgeType: edge.edge_type,
      edgeLabel: turnNodeIdSet.has(fromNode) && !toNode.startsWith('end:') ? 'Turn' : undefined,
      stepNumber: edge.step_number,
      contextId: edge.context_id,
      isConcurrent: edge.is_concurrent ?? false,
      status: edge?.status?.code?.toUpperCase() || '',
    };
  });

  // Add synthetic edge from start node to first turn
  if (firstTurnNodeId) {
    linkDataArray.unshift({
      id: `${startNodeKey}-to-${firstTurnNodeId}`,
      from: startNodeKey,
      to: firstTurnNodeId,
      fromPort: 'bottom',
      toPort: 'top',
      label: '',
      tooltip: '',
      edgeType: 'sequence',
      edgeLabel: '',
      stepNumber: undefined,
      contextId: undefined,
      isConcurrent: false,
      status: '',
    });
  }

  return { nodeDataArray, linkDataArray };
}

// ─────────────────────────────────────────────────────────────────────────────
// Turn children (agents/tools) projection function (step 2 of incremental split)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Project the children API response for a single turn into GoJS-compatible
 * nodeDataArray and linkDataArray that can be merged into the existing diagram.
 *
 * Children API returns the same shape as full graph: { graph_metadata, nodes[], edges[] }
 * but scoped to a single turn. We extract agents/tools, create a group node,
 * and produce the horizontal sub-graph for that turn.
 *
 * @param turnNodeId     - The turn node key in the diagram (used for group naming & turn→agent link)
 * @param childrenData   - Raw children API response { graph_metadata, nodes, edges }
 * @param questionsData  - QUESTIONS dataset for icon lookup
 * @param iconMap        - Map of entity types to icon URLs
 * @param turnY          - The Y position of the parent turn node (used for group positioning)
 */
export function projectTurnChildren(
  turnNodeId: string,
  childrenData: any,
  questionsData: any[] = [],
  iconMap: Record<string, string> = {},
  turnY?: number,
): { nodeDataArray: any[]; linkDataArray: any[]; turnNodeTray: Record<string, any> | null } {
  const empty = { nodeDataArray: [], linkDataArray: [], turnNodeTray: null };

  if (!childrenData || !childrenData.nodes || !childrenData.edges) {
    console.warn('[projectTurnChildren] Invalid or missing childrenData');
    return empty;
  }


  const groupKey = `group_${turnNodeId}`;

  // Filter to only agent/tool nodes (skip turn and end nodes from children response)
  const agentToolNodes = childrenData.nodes.filter((node: any) => {
    const type = inferNodeType(node);
    return type === 'agent' || type === 'tool';
  });

  if (agentToolNodes.length === 0) {
    console.warn('[projectTurnChildren] No agent/tool children found for turn', turnNodeId);
    return empty;
  }

  let turnNodeTray: Record<string, any> | null = null;

  const defaultIcon = iconMap['default'] || '/logos/provider/not-available.svg';

  // Build node data array for agents/tools, all assigned to the turn's group
  const nodeDataArray: any[] = agentToolNodes.map((node: any) => {
    const nodeType = inferNodeType(node);
    const label = node.name;
    let iconSource = defaultIcon;

    if (node.framework && iconMap[node.framework]) {
      iconSource = iconMap[node.framework];
    } else if (nodeType === 'agent' && node.system_prompt?.span_id) {
      iconSource = getIconSource(node.system_prompt.span_id, questionsData, iconMap);
    } else if (nodeType === 'tool' && node.description?.span_id) {
      iconSource = getIconSource(node.description.span_id, questionsData, iconMap);
    }

    return {
      key: node.id,
      label,
      name: node.name,
      type: nodeType,
      tooltip: '',
      category: nodeType,
      rawType: nodeType,
      metadata: node.metadata,
      source: iconSource,
      group: groupKey,
      // Agent-specific: pass system_prompt span reference as-is
      ...(nodeType === 'agent' && { systemPrompt: node.system_prompt ?? null }),
      // Tool-specific: pass description span reference as-is
      ...(nodeType === 'tool' && { description: node.description ?? null }),
    };
  });

  // Create the group node
  const groupY = turnY != null ? turnY : 100;
  const groupNode = {
    key: groupKey,
    isGroup: true,
    location: `${TURN_X + GROUP_X_OFFSET} ${groupY}`,
  };
  nodeDataArray.unshift(groupNode);

  // Build a set of agent/tool node IDs for filtering edges
  const childNodeIds = new Set(agentToolNodes.map((n: any) => n.id));

  // Derive per-node error status from incoming edges
  // A node has an error if any edge pointing TO it has status === 'ERROR'
  const errorNodeIds = new Set<string>();
  childrenData.edges.forEach((edge: any) => {
    if (edge?.status?.code?.toUpperCase() === 'ERROR') {
      const to = edge.to ?? edge.to_node;
      if (childNodeIds.has(to)) {
        errorNodeIds.add(to);
      }
    }
  });

  // Patch node data with error info
  const errorBadgeIcon = iconMap['error-badge'] || '/logos/error-badge.svg';
  nodeDataArray.forEach((nd: any) => {
    if (nd.isGroup) return;
    if (errorNodeIds.has(nd.key)) {
      nd.hasError = true;
      nd.errorBadge = errorBadgeIcon;
    } else {
      nd.hasError = false;
      nd.errorBadge = '';
    }
  });

  // Filter edges to only those connecting agent/tool nodes (skip turn-to-turn, turn-to-end)
  const relevantEdges = childrenData.edges.filter((edge: any) => {
    const from = edge.from ?? edge.from_node;
    const to = edge.to ?? edge.to_node;
    // Include edge if at least one endpoint is an agent/tool child
    return childNodeIds.has(from) || childNodeIds.has(to);
  });

  // Build link data array
  const linkDataArray: any[] = [];

  // Collect the turn node IDs from the children response (may differ from turnNodeId)
  const turnNodeIdsInResponse = new Set<string>();
  turnNodeIdsInResponse.add(turnNodeId); // Always include the diagram's turn key
  childrenData.nodes.forEach((node: any) => {
    const type = inferNodeType(node);
    if (type === 'turn') {
      turnNodeIdsInResponse.add(node.id);
    }
  });

  // Find edges from any turn node to any agent/tool child
  // The children API may reference the turn by a different ID than turnNodeId
  const turnOutgoingEdges = childrenData.edges.filter((edge: any) => {
    const from = edge.from ?? edge.from_node;
    const to = edge.to ?? edge.to_node;
    return turnNodeIdsInResponse.has(from) && childNodeIds.has(to);
  });

  // Track which edges we added as turn→agent links (to avoid duplicates later)
  const addedTurnEdgeIds = new Set<string>();

  // If the children API includes edges from turn → agent, use them
  // Remap `from` to the diagram's turnNodeId so GoJS can link them
  if (turnOutgoingEdges.length > 0) {
    turnOutgoingEdges.forEach((edge: any) => {
      linkDataArray.push({
        id: edge.id,
        from: turnNodeId, // Always use the diagram's turn key
        to: edge.to ?? edge.to_node,
        fromPort: 'right',
        toPort: 'left',
        label: '',
        tooltip: '',
        edgeType: edge.edge_type,
        stepNumber: edge.step_number,
        contextId: edge.context_id,
        isConcurrent: edge.is_concurrent ?? false,
        status: edge?.status?.code?.toUpperCase() || '',
        statusMsg: edge?.status?.message,
        statusObj: edge?.status,
        start_time: edge?.start_time,
        end_time: edge?.end_time,
        ...buildEdgeTrayData(edge),
      });
      addedTurnEdgeIds.add(edge.id);
    });
  }

  // Capture tray data for the turn node from the matching INVOKES edge.
  // Returned separately so the caller can find and update the existing turn node.
  const invokesEdge = turnOutgoingEdges.find(
    (edge: any) => edge.edge_type?.toUpperCase() === 'INVOKES'
  );
  if (invokesEdge) {
    turnNodeTray = buildTurnNodeTrayFromInvokesEdge(invokesEdge);
  }

  // Agent/tool ↔ agent/tool edges
  relevantEdges.forEach((edge: any) => {
    const from = edge.from ?? edge.from_node;
    const to = edge.to ?? edge.to_node;

    // Skip edges already added as turn outgoing
    if (addedTurnEdgeIds.has(edge.id)) return;

    // Skip edges originating from any turn node (already handled above)
    if (turnNodeIdsInResponse.has(from)) return;

    // Determine port connections
    const fromPort = 'right';
    const toPort = 'left';

    // For uses_tool edges, show truncated id with tooltip
    let label = '';
    let tooltip = '';
    const edgeType = edge.edge_type?.toLowerCase();
    if (edgeType === 'uses_tool') {
      const fullId = edge.id;
      tooltip = fullId;
      label = fullId?.length > 8 ? fullId.slice(0, 8) + '...' : fullId;
    }

    linkDataArray.push({
      id: edge.id,
      from,
      to,
      fromPort,
      toPort,
      label,
      tooltip,
      edgeType: edge.edge_type,
      stepNumber: edge.step_number,
      contextId: edge.context_id,
      isConcurrent: edge.is_concurrent ?? false,
      status: edge?.status?.code?.toUpperCase() || '',
      statusMsg: edge?.status?.message,
      statusObj: edge?.status,
      start_time: edge?.start_time,
      end_time: edge?.end_time,
      ...buildEdgeTrayData(edge),
    });
  });

  return { nodeDataArray, linkDataArray, turnNodeTray };
}